import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Loader2, AlertCircle, BookOpen, GitBranch, Activity } from 'lucide-react'
import api from '../../services/api'
import { renderContent } from '../../utils/contentRenderer'
import SkillContentTabs from '../SkillContentTabs'
import NodeDependencies from './NodeDependencies'
import NodePanelShell from './shared/NodePanelShell'
import {
  STATUS_COLORS,
  DIFFICULTY_LABELS,
  DIFFICULTY_BADGE,
  DOK_BY_DIFFICULTY,
  getMasteryPercent,
  getRemainingToMaster,
  getSkillAttentionMessage
} from './knowledgeMapUtils'

// Visual tone for the personalized skill message (getSkillAttentionMessage).
const MESSAGE_TONES = {
  attention: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
  progress: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300',
  success: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  info: 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300'
}

// Header tint by test type, mirroring the tutor/admin node header.
const TEST_TYPE_TINT = {
  math: { bg: 'bg-blue-50 dark:bg-blue-900/20', accent: 'text-blue-600 dark:text-blue-400' },
  reading_writing: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    accent: 'text-purple-600 dark:text-purple-400'
  }
}

function getMasteryMessage(status, score, difficulty) {
  if (status === 'MASTERED') return "You've mastered this skill!"
  if (status === 'UNVISITED') return 'Start practicing to build your mastery.'
  if (status === 'STRUGGLING') return 'You can do this! Focus on understanding the concepts.'
  const { remaining } = getRemainingToMaster(score, difficulty, status)
  return `${remaining} more correct answer${remaining !== 1 ? 's' : ''} to master this skill. Keep going!`
}

const TABS = [
  ['progress', 'My Progress', Activity],
  ['content', 'Concept Content', BookOpen],
  ['relationships', 'Relationships', GitBranch]
]

/**
 * Personal knowledge-map node detail, shown as a side panel beside the graph (the tutor pattern). Used
 * by BOTH the student's own map and the tutor's per-student drill-down, and — via `nodeApiBase` — the
 * guardian's read-only view of a child's map. Exposes ONLY student-appropriate tabs:
 *  - My Progress: the learner's own mastery + an illustrative sample question (never answers).
 *  - Concept Content: the node's full teaching card incl. images, via SkillContentTabs (citable=false,
 *    so the admin-only illustration-ID badge is hidden but illustrations still render).
 *  - Relationships: prerequisites/dependents resolved client-side from the loaded nodes.
 * There is NO Questions tab and NO population/cohort Analytics tab.
 *
 * @param {Object}   node       - the selected node (skillGroupKey, skill, domain, difficulty, mastery)
 * @param {Function} onClose    - close the panel
 * @param {Array}    allNodes   - every node loaded for this test type (for the Relationships tab)
 * @param {Function} onNavigate - (skillGroupKey) => void, re-target the panel to another node
 * @param {string}   nodeApiBase - base path for the teaching-content fetch. Defaults to the student
 *   endpoint; the guardian view passes `/family/children/:childId/knowledge-map` so the same panel
 *   reads the child's map. The sample-question fetch is always the generic student endpoint.
 */
export default function NodeDetailPanel({
  node,
  onClose,
  allNodes = [],
  onNavigate,
  nodeApiBase = '/knowledge-map'
}) {
  const [tab, setTab] = useState('progress')
  const [card, setCard] = useState(null)
  const [cardLoading, setCardLoading] = useState(false)
  const [cardError, setCardError] = useState(false)
  const [sampleQuestion, setSampleQuestion] = useState(null)
  const [sampleLoading, setSampleLoading] = useState(false)
  const [sampleError, setSampleError] = useState(false)

  const skillGroupKey = node?.skillGroupKey
  const isSkillNode = !!node?.isSkillNode
  // A consolidated skill node's skillGroupKey is a synthetic "test:domain:skill" key the server does
  // not know. Its card + sample question target the learner's CURRENT DOK variant, whose skillGroupKey
  // IS a real node id — so both fetches use that variant key (identical to skillGroupKey otherwise).
  const contentKey = isSkillNode
    ? node?.dokBreakdown?.[node?.currentDok?.difficulty]?.skillGroupKey || null
    : skillGroupKey

  // Reset to the first tab whenever a different node is opened.
  useEffect(() => {
    setTab('progress')
  }, [skillGroupKey])

  // Load the teaching card (concept content + illustrations) for the Concept Content tab.
  useEffect(() => {
    if (!contentKey) return
    let cancelled = false
    setCardLoading(true)
    setCardError(false)
    setCard(null)
    api
      .get(`${nodeApiBase}/node/${encodeURIComponent(contentKey)}`)
      .then((res) => {
        if (cancelled) return
        setCard(res.data?.data?.card || null)
      })
      .catch(() => {
        if (cancelled) return
        setCardError(true)
      })
      .finally(() => {
        if (!cancelled) setCardLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [contentKey, nodeApiBase])

  // Load one sample question for the My Progress tab. The sample question is generic (not learner
  // specific), so it always uses the student endpoint even in the guardian view.
  useEffect(() => {
    if (!contentKey) return
    let cancelled = false
    setSampleLoading(true)
    setSampleError(false)
    setSampleQuestion(null)
    api
      .get(`/knowledge-map/sample-question/${encodeURIComponent(contentKey)}`)
      .then((res) => {
        if (cancelled) return
        setSampleQuestion(res.data?.data?.question || null)
      })
      .catch(() => {
        if (cancelled) return
        setSampleError(true)
      })
      .finally(() => {
        if (!cancelled) setSampleLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [contentKey])

  if (!node) return null

  const mastery = node.mastery || { status: 'UNVISITED', score: 0, totalAttempts: 0 }
  const colors = STATUS_COLORS[mastery.status] || STATUS_COLORS.UNVISITED
  const badge = DIFFICULTY_BADGE[node.difficulty] || DIFFICULTY_BADGE.M
  const diffLabel = DIFFICULTY_LABELS[node.difficulty] || node.difficulty
  const tint = TEST_TYPE_TINT[node.testType] || TEST_TYPE_TINT.math
  const score = mastery.score || 0
  const pct = getMasteryPercent(score)

  // Personalized skill message (skill nodes only).
  const skillMessage = isSkillNode ? getSkillAttentionMessage(node, { allNodes }) : null

  return (
    <NodePanelShell
      tint={tint}
      breadcrumb={`${node.domain} | ${node.testType === 'reading_writing' ? 'Reading & Writing' : 'Math'}`}
      title={node.skill}
      onClose={onClose}
      tabs={TABS.map(([id, label, Icon]) => ({ id, label, Icon }))}
      activeTab={tab}
      onTabChange={setTab}
      badges={
        <>
          {isSkillNode ? (
            <span
              className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
              data-testid="current-dok-badge"
            >
              {node.currentDok?.allMastered
                ? 'All DOK levels mastered'
                : `Currently at ${node.currentDok?.label || 'DOK 1'}`}
            </span>
          ) : (
            <span
              className="px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: badge.bg, color: badge.text }}
            >
              {diffLabel}
            </span>
          )}
          <span
            className="flex items-center gap-1 text-xs font-medium"
            style={{ color: colors.border }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.dot }} />
            {colors.text}
          </span>
        </>
      }
    >
      {/* Content */}
      <div className="p-5">
        {/* My Progress */}
        {tab === 'progress' && (
          <div className="space-y-4">
            {/* Personalized skill message ("needs attention" / progress / readiness) */}
            {skillMessage && (
              <p
                data-testid="skill-attention-message"
                className={`text-sm rounded-lg p-3 leading-relaxed font-medium ${
                  MESSAGE_TONES[skillMessage.tone] || MESSAGE_TONES.info
                }`}
              >
                {skillMessage.text}
              </p>
            )}

            {/* Per-DOK breakdown (skill nodes): each level's own status and progress. */}
            {isSkillNode && (
              <div className="space-y-1.5" data-testid="dok-breakdown">
                {['E', 'M', 'H'].map((d) => {
                  const entry = node.dokBreakdown?.[d]
                  if (!entry) return null
                  const m = entry.mastery || { status: 'UNVISITED', score: 0, totalAttempts: 0 }
                  const c = STATUS_COLORS[m.status] || STATUS_COLORS.UNVISITED
                  const isCurrent = node.currentDok?.difficulty === d
                  return (
                    <div
                      key={d}
                      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${
                        isCurrent ? 'border-2' : 'border border-gray-200 dark:border-gray-700'
                      }`}
                      style={isCurrent ? { borderColor: c.border } : undefined}
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.dot }} />
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {DOK_BY_DIFFICULTY[d]}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({DIFFICULTY_LABELS[d]})
                          {isCurrent && !node.currentDok?.allMastered ? ' — you are here' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-xs text-gray-500">
                        {m.totalAttempts > 0
                          ? `${Math.round((m.score || 0) * 100)}% · ${m.totalAttempts} attempts`
                          : 'Not started'}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Raw correct/total is only meaningful per knowledge_id. For a skill node these counters
                are summed across difficulty variants (UserNodeMastery double-counts cross-variant), so
                the ratio would disagree with the mean-score "Overall Skill Progress" bar below - hide
                it and let the averaged bar speak for skill nodes. */}
            {!isSkillNode && mastery.totalAttempts > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Correct Answers</span>
                <span className="font-mono text-gray-800 dark:text-gray-200">
                  {mastery.correctAttempts}/{mastery.totalAttempts}
                  <span className="text-gray-400 ml-1">
                    ({Math.round((mastery.correctAttempts / mastery.totalAttempts) * 100)}%)
                  </span>
                </span>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-gray-600 dark:text-gray-400">
                  {isSkillNode ? 'Overall Skill Progress' : 'Mastery Progress'}
                </span>
                <span className="font-mono text-gray-500">{(score * 100).toFixed(0)}% / 70%</span>
              </div>
              <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: colors.dot }}
                />
              </div>
            </div>

            {!isSkillNode && (
              <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 leading-relaxed">
                {getMasteryMessage(mastery.status, score, node.difficulty)}
              </p>
            )}

            <p className="text-xs text-gray-400 leading-relaxed border-t border-gray-100 dark:border-gray-700 pt-3">
              Mastery is built by answering correctly. Harder questions contribute more: Easy +10%,
              Medium +15%, Hard +25%. Reach 70% to master a skill.
            </p>

            {/* Sample Question — illustrative only, never answers */}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                Sample Question
              </span>
              <p className="text-xs text-gray-400 mt-0.5 mb-2">
                An example of what this skill tests.
              </p>
              {sampleLoading && (
                <div className="h-12 bg-gray-50 dark:bg-gray-900 rounded animate-pulse" />
              )}
              {sampleError && (
                <p className="text-sm text-gray-400">Unable to load sample question.</p>
              )}
              {!sampleLoading && !sampleError && sampleQuestion && (
                <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 leading-relaxed">
                  {sampleQuestion.context && (
                    <div className="mb-2 text-gray-600 dark:text-gray-400 italic">
                      {renderContent(sampleQuestion.context, sampleQuestion.contentType || 'md')}
                    </div>
                  )}
                  <div>
                    {renderContent(sampleQuestion.questionText, sampleQuestion.contentType || 'md')}
                  </div>
                </div>
              )}
              {!sampleLoading && !sampleError && !sampleQuestion && (
                <p className="text-sm text-gray-400">
                  No sample questions available for this skill yet.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Concept Content — full teaching card incl. illustrations */}
        {tab === 'content' && (
          <>
            {cardLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            ) : cardError ? (
              <div className="flex items-center justify-center py-12 text-red-500">
                <AlertCircle className="w-5 h-5 mr-2" />
                Failed to load concept content.
              </div>
            ) : card ? (
              <SkillContentTabs
                skill={card}
                citable={false}
                expandedQuestions={{}}
                toggleQuestion={() => {}}
                reviewedIds={[]}
                getAnswerLabel={() => ''}
              />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No concept content available for this skill yet.
              </p>
            )}
          </>
        )}

        {/* Relationships — resolved from the loaded node set */}
        {tab === 'relationships' && (
          <NodeDependencies node={node} allNodes={allNodes} onNavigate={onNavigate} />
        )}
      </div>
    </NodePanelShell>
  )
}

NodeDetailPanel.propTypes = {
  node: PropTypes.shape({
    skillGroupKey: PropTypes.string,
    skill: PropTypes.string,
    domain: PropTypes.string,
    difficulty: PropTypes.string,
    testType: PropTypes.string,
    prerequisites: PropTypes.arrayOf(PropTypes.string),
    mastery: PropTypes.object,
    // Consolidated skill nodes (buildSkillGraphNodes)
    isSkillNode: PropTypes.bool,
    currentDok: PropTypes.object,
    dokBreakdown: PropTypes.object
  }),
  onClose: PropTypes.func.isRequired,
  allNodes: PropTypes.array,
  onNavigate: PropTypes.func,
  nodeApiBase: PropTypes.string
}
